import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { SearchIndex, type SearchResult } from '../lib/search';
import type { Landmark } from '../types/landmark';
import type { Business } from '../types/business';
import type { Place } from '../types/place';

interface SearchBarProps {
  landmarks: Landmark[];
  businesses: Business[];
  places: Place[];
  onSelect: (result: SearchResult) => void;
}

const RESULT_LIMIT = 8;

export function SearchBar({ landmarks, businesses, places, onSelect }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const listboxId = useId();

  const index = useMemo(() => new SearchIndex(landmarks, businesses, places), [landmarks, businesses, places]);
  const results = useMemo(() => index.search(query, RESULT_LIMIT), [index, query]);

  // Reset highlight whenever the result list changes shape.
  useEffect(() => {
    setHighlight(0);
  }, [results]);

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onDocClick);
    return () => window.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
      setOpen(true);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
      setOpen(true);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = results[highlight];
      if (target) {
        onSelect(target);
        setQuery('');
        setOpen(false);
      }
    }
  };

  const showResults = open && query.trim().length >= 2;

  return (
    <div
      ref={wrapperRef}
      data-testid="search-bar"
      style={{
        position: 'absolute',
        top: 'var(--space-3)',
        left: 'var(--space-3)',
        zIndex: 20,
        width: 'min(360px, calc(100vw - var(--space-6)))',
      }}
    >
      <label htmlFor={inputId} className="sr-only" style={visuallyHidden}>
        Search landmarks and businesses
      </label>
      <div style={{ position: 'relative' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            display: 'inline-flex',
          }}
        >
          <svg
            width={16}
            height={16}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="M16.5 16.5L21 21" />
          </svg>
        </span>
        <input
          ref={inputRef}
          id={inputId}
          data-testid="search-input"
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder="Search landmarks or places…"
          aria-label="Search landmarks and businesses"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={showResults}
          autoComplete="off"
          spellCheck={false}
          style={{
            width: '100%',
            padding: '10px 12px 10px 36px',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            fontSize: '0.95rem',
            color: 'var(--text)',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {showResults ? (
        <ul
          id={listboxId}
          role="listbox"
          data-testid="search-results"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            listStyle: 'none',
            padding: 'var(--space-1)',
            margin: 0,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-popover)',
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
        >
          {results.length === 0 ? (
            <li
              data-testid="search-empty"
              style={{
                padding: 'var(--space-3)',
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
              }}
            >
              No matches in {index.size().total.toLocaleString()} indexed records.
            </li>
          ) : (
            results.map((r, i) => (
              <li
                key={`${r.kind}-${r.id}`}
                role="option"
                aria-selected={i === highlight}
                data-testid={`search-result-${r.kind}-${r.id}`}
                onMouseDown={(e) => {
                  // mousedown (not click) so the input's onBlur from our outside-click
                  // handler doesn't fire before the selection is committed.
                  e.preventDefault();
                  onSelect(r);
                  setQuery('');
                  setOpen(false);
                }}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  background: i === highlight ? 'var(--surface-hover)' : 'transparent',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 'var(--space-2)',
                  alignItems: 'baseline',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.name}
                  </div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.subtitle}
                  </div>
                </div>
                <ResultBadge kind={r.kind} />
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

function ResultBadge({ kind }: { kind: SearchResult['kind'] }) {
  const styles = {
    business: { background: 'var(--accent)', color: '#1a1410', label: 'Tavern' },
    place: { background: 'var(--secondary)', color: '#fffdf2', label: 'Place' },
    landmark: { background: 'var(--surface-hover)', color: 'var(--text-muted)', label: 'Heritage' },
  } as const;
  const s = styles[kind];
  return (
    <span
      style={{
        fontSize: '0.65rem',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: 'var(--radius-sm)',
        background: s.background,
        color: s.color,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;
